import React, { useState, useEffect, useRef } from "react";
import { useApp } from "../../context/AppContext";
import { Search, Bell, Menu, X } from "lucide-react";
import { CURRENCIES } from "../../utils/constants";
import { formatINR, formatDate, timeAgo } from "../../utils/helpers";

const PAGE_NAMES = {
  dashboard: "Dashboard", expenses: "Expenses", crypto: "Crypto Portfolio",
  prices: "Live Prices", team: "Team Members", payroll: "Payroll",
  loans: "Loan Requests", audit: "Audit Log", security: "Security",
};

export default function Topbar({ activePage, setActivePage, mobile }) {
  const { currency, setCurrency, mode, setSidebarOpen, expenses, team, auditLog, loanRequests } = useApp();
  const [showSearch, setShowSearch] = useState(false);
  const [showNotif, setShowNotif] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);

  // Close modals on custom event
  useEffect(() => {
    const h = () => { setShowSearch(false); setShowNotif(false); setQuery(""); };
    window.addEventListener("closeModals", h);
    return () => window.removeEventListener("closeModals", h);
  }, []);

  // Auto-focus search input
  useEffect(() => {
    if (showSearch && inputRef.current) setTimeout(() => inputRef.current && inputRef.current.focus(), 100);
  }, [showSearch]);

  // Ctrl+K shortcut
  useEffect(() => {
    const h = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setShowSearch(true);
        setShowNotif(false);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  // Search logic
  const q = query.toLowerCase().trim();
  const results = [];
  if (q.length >= 2) {
    (expenses || []).filter(e =>
      (e.description || "").toLowerCase().includes(q) ||
      (e.category || "").toLowerCase().includes(q) ||
      String(e.amount).includes(q)
    ).slice(0, 5).forEach(e => {
      results.push({ type: "expense", label: (e.description || e.category || "Expense") + " — " + formatINR(e.amount), sub: formatDate(e.date), page: "expenses" });
    });
    (team || []).filter(m => m.name.toLowerCase().includes(q) || m.role.toLowerCase().includes(q)).slice(0, 5).forEach(m => {
      results.push({ type: "team", label: m.name + " — " + m.role, sub: m.status, page: "team" });
    });
    (loanRequests || []).filter(l => l.employee.toLowerCase().includes(q) || (l.reason || "").toLowerCase().includes(q)).slice(0, 3).forEach(l => {
      results.push({ type: "loan", label: l.employee + " — " + formatINR(l.amount), sub: l.status, page: "loans" });
    });
    Object.entries(PAGE_NAMES).forEach(([key, name]) => {
      if (name.toLowerCase().includes(q)) results.push({ type: "page", label: "Go to " + name, sub: "Page", page: key });
    });
  }

  // Notifications from audit log
  const notifications = (auditLog || []).slice(-20).reverse().map(e => ({
    id: e.id,
    text: e.action.replace(/_/g, " "),
    actor: e.actor,
    time: timeAgo(e.timestamp),
    type: e.action.includes("FAIL") || e.action.includes("LOCK") ? "danger" : e.action.includes("DELETE") || e.action.includes("REMOVE") ? "warning" : "info",
  }));
  const unread = (auditLog || []).filter(e => (Date.now() - new Date(e.timestamp).getTime()) < 3600000).length;

  const goToPage = (page) => {
    if (setActivePage) setActivePage(page);
    setShowSearch(false);
    setQuery("");
  };

  const typeColors = { expense: "#F5A623", team: "#3B82F6", loan: "#F97316", page: "#10B981" };
  const typeBgs = { expense: "rgba(245,166,35,.12)", team: "rgba(59,130,246,.12)", loan: "rgba(249,115,22,.12)", page: "rgba(16,185,129,.12)" };

  // Styles
  const topbarStyle = {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: mobile ? "10px 12px" : "12px 24px",
    borderBottom: "1px solid rgba(255,255,255,.06)",
    background: "var(--card, #0B0F1A)",
    position: "sticky", top: 0, zIndex: 50,
  };

  const btnStyle = {
    width: 36, height: 36, borderRadius: 8,
    background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.08)",
    color: "#94A3B8", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
  };

  return (
    <>
      {/* TOPBAR */}
      <header style={topbarStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {mobile && (
            <button onClick={() => setSidebarOpen(true)} style={{ background: "none", border: "none", color: "#E2E8F0", cursor: "pointer", padding: 6, display: "flex", alignItems: "center" }}>
              <Menu size={22} />
            </button>
          )}
          <div>
            <h2 style={{ fontSize: mobile ? 15 : 18, fontWeight: 700, color: "#E2E8F0", margin: 0 }}>
              {PAGE_NAMES[activePage] || "Dashboard"}
            </h2>
            {!mobile && (
              <span style={{ fontSize: 11, color: "#64748B" }}>
                {mode === "admin" ? "Admin" : "Employee"} &gt; {PAGE_NAMES[activePage]}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: mobile ? 6 : 10 }}>
          {!mobile && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#10B981", fontWeight: 600 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#10B981" }} />
              Live
            </div>
          )}
          {!mobile && (
            <select value={currency} onChange={(e) => setCurrency(e.target.value)} style={{ background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.1)", borderRadius: 8, color: "#E2E8F0", padding: "6px 10px", fontSize: 12, cursor: "pointer" }}>
              {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.symbol} {c.name}</option>)}
            </select>
          )}
          <button onClick={() => { setShowSearch(true); setShowNotif(false); }} style={btnStyle} title="Search (Ctrl+K)">
            <Search size={16} />
          </button>
          <button onClick={() => { setShowNotif(!showNotif); setShowSearch(false); }} style={{ ...btnStyle, position: "relative" }}>
            <Bell size={16} />
            {unread > 0 && (
              <span style={{ position: "absolute", top: -3, right: -3, minWidth: 16, height: 16, borderRadius: 8, background: "#EF4444", color: "#fff", fontSize: 9, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 4px" }}>
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </button>
        </div>
      </header>

      {/* SEARCH MODAL */}
      {showSearch && (
        <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: mobile ? "5vh" : "12vh" }} onClick={() => { setShowSearch(false); setQuery(""); }}>
          <div style={{ background: "#0F1629", border: "1px solid rgba(255,255,255,.1)", borderRadius: 16, width: mobile ? "95%" : 540, maxWidth: 540, boxShadow: "0 20px 60px rgba(0,0,0,.6)", overflow: "hidden" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", borderBottom: "1px solid rgba(255,255,255,.08)" }}>
              <Search size={18} style={{ color: "#64748B", flexShrink: 0 }} />
              <input ref={inputRef} type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search expenses, team, pages..." style={{ flex: 1, background: "none", border: "none", outline: "none", color: "#E2E8F0", fontSize: 15, fontFamily: "inherit" }} />
              <button onClick={() => { setShowSearch(false); setQuery(""); }} style={{ background: "none", border: "none", color: "#64748B", cursor: "pointer", padding: 4, display: "flex" }}>
                <X size={16} />
              </button>
            </div>
            <div style={{ maxHeight: 340, overflowY: "auto", padding: 6 }}>
              {q.length >= 2 ? (
                results.length > 0 ? results.map((r, i) => (
                  <div key={i} onClick={() => goToPage(r.page)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderRadius: 10, cursor: "pointer" }}>
                    <span style={{ fontSize: 9, fontWeight: 800, textTransform: "uppercase", padding: "3px 8px", borderRadius: 4, background: typeBgs[r.type], color: typeColors[r.type] }}>{r.type}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#E2E8F0", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.label}</div>
                      <div style={{ fontSize: 11, color: "#64748B" }}>{r.sub}</div>
                    </div>
                  </div>
                )) : <div style={{ padding: 28, textAlign: "center", color: "#64748B", fontSize: 13 }}>No results for "{query}"</div>
              ) : (
                <div style={{ padding: 28, textAlign: "center", color: "#475569", fontSize: 13 }}>Type 2+ characters to search</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* NOTIFICATIONS PANEL */}
      {showNotif && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 180 }} onClick={() => setShowNotif(false)} />
          <div style={{ position: "fixed", top: 58, right: mobile ? 10 : 16, zIndex: 190, width: mobile ? "calc(100vw - 20px)" : 340, maxHeight: 440, background: "#0F1629", border: "1px solid rgba(255,255,255,.1)", borderRadius: 14, boxShadow: "0 16px 48px rgba(0,0,0,.5)", overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderBottom: "1px solid rgba(255,255,255,.08)" }}>
              <strong style={{ color: "#E2E8F0", fontSize: 14 }}>Notifications</strong>
              <span style={{ color: "#64748B", fontSize: 12 }}>{notifications.length} events</span>
            </div>
            <div style={{ overflowY: "auto", padding: 6, maxHeight: 360 }}>
              {notifications.length > 0 ? notifications.slice(0, 15).map((n) => (
                <div key={n.id} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 12px", borderRadius: 8 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", marginTop: 5, flexShrink: 0, background: n.type === "danger" ? "#EF4444" : n.type === "warning" ? "#F97316" : "#3B82F6" }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, color: "#CBD5E1", lineHeight: 1.4 }}>
                      <strong style={{ color: "#E2E8F0" }}>{n.actor}</strong> — {n.text}
                    </div>
                    <div style={{ fontSize: 10, color: "#475569", marginTop: 2 }}>{n.time}</div>
                  </div>
                </div>
              )) : (
                <div style={{ padding: 28, textAlign: "center", color: "#64748B", fontSize: 13 }}>No notifications yet</div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}
